import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Edit2, Trash2, Lightbulb, HelpCircle, BookOpen, Clock, Calendar } from 'lucide-react';
import { foodProfiles } from '../data/foodProfiles';
import { recipes } from '../data/recipes';
import { supplements } from '../data/supplements';
import { lifestyleHabits } from '../data/lifestyleHabits';

const NOTE_TYPE_CONFIG = {
  'learning': { label: 'Learning / Insight', icon: Lightbulb, color: 'text-green-700', bg: 'bg-green-50' },
  'question': { label: 'Question for Doctor', icon: HelpCircle, color: 'text-red-700', bg: 'bg-red-50' },
  'reflection': { label: 'Personal Reflection', icon: BookOpen, color: 'text-blue-700', bg: 'bg-blue-50' },
  'revisit': { label: 'Something to Revisit', icon: Clock, color: 'text-amber-700', bg: 'bg-amber-50' }
};

const NoteCard = ({ note, onEdit, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const typeConfig = NOTE_TYPE_CONFIG[note.note_type] || NOTE_TYPE_CONFIG.learning;
  const TypeIcon = typeConfig.icon;

  // Get linked items names
  const getLinkedItemNames = () => {
    if (!note.linked_context || note.linked_context.length === 0) {
      return [];
    }

    const allItems = [
      ...foodProfiles,
      ...recipes,
      ...supplements,
      ...lifestyleHabits
    ];

    return note.linked_context
      .map(itemId => {
        const item = allItems.find(i => i.id === itemId);
        return item ? item.name : null;
      })
      .filter(Boolean);
  };

  const linkedItems = getLinkedItemNames();

  // Format date
  const formatDate = (isoDate) => {
    try {
      const date = new Date(isoDate);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return isoDate;
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all">
      {/* Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 flex items-start justify-between text-left hover:bg-slate-50 transition-colors rounded-xl"
      >
        <div className="flex-1">
          {/* Title or First Line */}
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            {note.title || note.content.substring(0, 60) + (note.content.length > 60 ? '...' : '')}
          </h3>
          
          {/* Type Badge */}
          <div className="flex items-center gap-2 mb-2">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${typeConfig.bg} ${typeConfig.color} text-sm font-medium rounded-full`}>
              <TypeIcon className="h-4 w-4" />
              {typeConfig.label}
            </span>
          </div>

          {/* Date */}
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Calendar className="h-4 w-4" />
            {formatDate(note.date_created)}
          </div>
        </div>
        
        <div className="ml-4 flex items-center gap-2">
          {isExpanded ? (
            <ChevronUp className="h-6 w-6 text-slate-400" />
          ) : (
            <ChevronDown className="h-6 w-6 text-slate-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-4 border-t border-slate-100 pt-6">
          {/* Full Content */}
          {note.title && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                Note Content
              </h4>
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                {note.content}
              </p>
            </div>
          )}

          {/* Linked Context */}
          {linkedItems.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                Related To
              </h4>
              <div className="flex flex-wrap gap-2">
                {linkedItems.map((itemName, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1.5 bg-slate-50 text-slate-700 text-sm rounded-full border border-slate-200"
                  >
                    {itemName}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(note);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
            >
              <Edit2 className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(note.id);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs text-slate-700 leading-relaxed">
              This note is a personal reflection and does not constitute medical advice.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoteCard;
